import { useContext, useEffect, useRef, useState } from 'react';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { RootState } from '../../../Redux/Store';
import { AppContext } from '../../../Context/AppContext';
import { useAppDispatch, useAppSelector } from '../../../Hooks/Hooks';
import {
  bookLoad,
  getSearchLoads,
  handleResetSearchQueryError,
  updateSearchQuery,
} from '../../../Redux/Reducers/SearchSlice';
import { getLoadData } from '../../../Redux/Reducers/MyLoadsSlice';
import { getAvailableAnchorShipments } from '../../../Redux/Reducers/LoadSlice';
import { EVENT, SCREEN, TAB } from '../../../Constants/AnalyticsConstants';
import {
  APPOINTMENT_TOOLTIP_MESSAGE,
  BookingFailed,
  CANCEL_TEXT,
  dropDateOffset,
  OK_TEXT,
  pickupDateOffset,
  RefreshResults,
  SERVER_ERROR_MESSAGES,
  UpdateNow,
  ANYWHERE,
  LAST_SHIPMENT_MODAL_MESSAGE,
} from '../../../Constants/Constants';
import { fbLogEvent } from '../../../Helpers/FirebaseHelper';
import { convertCurrencyFormat, roundToNearestWholeNumber } from '../../../Utils/Basic';
import {
  commonDateFormat,
  dayjs,
  isBeforeCurrentDate,
  getNewDates,
  dataBaseFriendlyDateFormat,
  timezone,
  utc,
  getTimeFormatAccordingToPreference,
} from '../../../Utils/DateUtils';
import {
  deadHeadValidation,
  getMemberDetails,
  isAppointmentState,
  isValidRPM,
} from '../../../Utils/Search';
import { ELoadCardSize, TLoadCardProps } from './LoadCard.type';
import CustomModal from '../CustomModal/CustomModal';
import ReloadButton from '../ReloadButton/ReloadButton';
import TermsAndConditions from '../TermsAndConditions/TermsAndConditions';
import Tooltip from '../Tooltip/Tooltip';
import ExpandedViewContainer from './ExpandedViewContainer/ExpandedViewContainer';
import LoadRateContainer from './ExpandedViewContainer/LoadRateContainer/LoadRateContainer';
import AdvancedSearchModal from './AdvancedSearchModal/AdvancedSearchModal';
import './LoadCard.scss';
import { isEntitled } from '../../../Utils/ContentOrConfigUtil';
import './ExpandedViewContainer/LoadRateContainer/LoadRateContainer.scss';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

const TopRightIcon = (
  data: any,
  isMobile: boolean,
  isWatchedLoads?: boolean,
  isRecent?: boolean,
  isFavorite?: boolean,
  isSearchResults?: boolean,
  isMyLoads?: boolean,
) => {
  if (isWatchedLoads || isSearchResults) {
    return (
      <>
        <img
          src={`/oo/assets/icon/${data?.isWatched ? 'watched-desktop' : 'watch-icon'}.svg
        `}
          alt={'WatcherIcon'}
        />
        {!isMobile && <span>{data?.isWatched ? 'Unwatch' : 'Watch'}</span>}
      </>
    );
  }
  if (isMyLoads) {
    return (
      <div>
        <button
          className="secondary-cta-btn"
          style={{ backgroundColor: 'transparent' }}
          onClick={() => {
            toast('Coming soon');
          }}
        >
          Un-Assign Driver
        </button>
      </div>
    );
  }
  if (isRecent || isFavorite) {
    return (
      <>
        <img
          src={`/oo/assets/icon/feedback-star${data?.favouriteSearchIndicator ? '-filled' : ''}.svg
        `}
          alt={isRecent ? 'RecentIcon' : 'FavoriteIcon'}
        />
        {!isMobile && <span>{data?.favouriteSearchIndicator ? 'Unfavorite' : 'Favorite'}</span>}
      </>
    );
  }
  return null;
};
const RightPartBottomButton = (
  isMobile: boolean,
  showBottomButton: boolean,
  isWatchedLoads?: boolean,
  isSearchResults?: boolean,
  isMyLoads?: boolean,
  isBinRateAvailable?: boolean,
  hasAppointment?: boolean,
  handleBottomClick?: () => void,
  handleBookNowClick?: () => void,
) => {
  if (isMobile) return null;

  return (
    <div
      data-testid="Right_Part_Bottom"
      className={`Right_Part_bottom ${
        showBottomButton &&
        (!(isWatchedLoads || isSearchResults) ||
          ((isWatchedLoads || isSearchResults) && isBinRateAvailable && hasAppointment))
          ? 'visible'
          : ''
      }`}
    >
      <button
        data-testid="Right_Part_Bottom_Button"
        className={`${
          isWatchedLoads || isSearchResults || isMyLoads ? 'primary-cta-btn' : 'secondary-cta-btn'
        }`}
        onClick={
          isBinRateAvailable && hasAppointment
            ? handleBookNowClick
            : handleBottomClick ||
              (() => {
                toast('Coming soon');
              })
        }
      >
        {!(isWatchedLoads || isSearchResults || isMyLoads)
          ? 'See Results'
          : isBinRateAvailable && hasAppointment
            ? 'Book Now'
            : isMyLoads
              ? 'Update'
              : ''}
      </button>
    </div>
  );
};
const LoadCard = ({
  isInsertShipment = false,
  withoutDateTime = false,
  withoutYear = false,
  showDateTimeRightSection = false,
  data,
  isRecent = false,
  isResultsCard = false,
  isFavorite = false,
  isWatchedLoads = false,
  isSearchResults = false,
  isMyLoads = false,
  isExpanded = false,
  showDropDown = false,
  showReload = false,
  showRate = false,
  showBottomButton = false,
  hideRightTopPart = false,
  handleBottomClick,
  handleReload,
  handleRightTopButtonClick,
  handleCardClick,
  size = ELoadCardSize.large,
  cardClickEnabled = false,
  additionalCssClass,
  hideSpecialServices = false,
  hideDeadHead = false,
  showRateLeft = false,
  hideRightPart = false,
  showLoadIdLeftMobile = false,
  hideSpecialInfoLeft = false,
  isRecentlyDelivered = false,
  showAdvancedSearchData = false,
  showNewUpdateFlag = false,
  isGlobalSearch = false,
}: TLoadCardProps) => {
  const { isMobile } = useContext(AppContext);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { configTableData } = useAppSelector((state: RootState) => state.ContentOrConfig);
  const { loadDetails } = useAppSelector((state: RootState) => {
    return state?.myLoads;
  });
  const { lastLocation } = useAppSelector((state: RootState) => state.NatNal);
  const appConfigData = configTableData?.FPO_APP_CONFIG;
  const [dropDownIndicator, setDropDownIndicator] = useState('down');
  const [bookNowModalProps, setBookNowModalProps] = useState({
    isModalOpen: false,
  });
  const [thankYouModalProps, setThankYouModalProps] = useState({
    isModalOpen: false,
  });
  const [warningModalProps, setWarningModalProps] = useState({
    isModalOpen: false,
  });
  const [insertShipmentModalProps, setInsertShipmentModalProps] = useState<any>({
    isModalOpen: false,
    buttons: [],
  });
  const [isAdvancedTooltipActive, setIsAdvancedTooltipActive] = useState(false);
  const [advanceSearchTooltip, setAdvanceSearchTooltip] = useState<string | null | React.ReactNode>(
    null,
  );
  const [lastShipmentWarningModal, setLastShipmentWarningModal] = useState({
    isModalOpen: false,
  });
  const selectedAnchor = useRef<string | null>(null);
  let showAllLoadExclusions = false;

  const { memberData } = useAppSelector((state: RootState) => state.member);
  const { availableAnchorShipments } = useAppSelector((state: RootState) => state.Load);
  const { searchQuery, sortAndFilter } = useAppSelector((state: RootState) => state?.search);
  const memberDetails = getMemberDetails(memberData, isMobile);
  const [tooltip, setTooltip] = useState('');
  const [isShipmentSelected, setIsShipmentSelected] = useState(false);
  const [advancedSearchModalProps, setAdvancedSearchModal] = useState({
    isModalOpen: false,
  });

  const handleShipmentSelection = (selected: boolean) => {
    setIsShipmentSelected(selected);
  };

  const renderTooltipContent = () => {
    return (
      <div>
        {data?.excludeSpecialServices?.map((item: any, index: number) => (
          <div key={index}>- {item}</div>
        ))}
      </div>
    );
  };

  const tooltipIcon = (
    <img src="/oo/assets/icon/alert-app.svg" alt="Alert icon" className="Alert_Tooltip_Icon" />
  );
  const isAppointment = isAppointmentState(data?.loadStop) || data?.isAppointmentConfirmed;

  const payload = {
    driverId: memberData?.member?.userApplicationDetails?.[0].attributes.PrimaryBookingDriverId,
    loadId: data?.loadId,
  };

  useEffect(() => {
    if (isAdvancedTooltipActive) {
      setAdvanceSearchTooltip(renderTooltipContent());
    } else {
      setAdvanceSearchTooltip(null);
    }
  }, [isAdvancedTooltipActive]);
  const isLastShipment = (lastLocation: any, availableAnchorShipments: any) => {
    return availableAnchorShipments.some(
      (shipment: any) => shipment.shipmentId === lastLocation?.shipmentId,
    );
  };

  const handleViewMoreAdvancedSearchClick = () => {
    const advancedSearchModalData = {
      isModalOpen: true,
      title: 'Advance Search',
      titleIcon: '/oo/assets/icon/info.svg',
      contentAlignment: 'left',
      additionalCssClass:
        'AdvancedSearch_Modal AdvancedSearch_Modal_Content AdvancedSearch_Content_Grid button',
      children: <AdvancedSearchModal data={data} />,
      buttons: [
        {
          title: 'Ok',
          type: 'primary-cta-btn',
          onClick: () => {
            setAdvancedSearchModal({ isModalOpen: false });
          },
        },
      ],
    };
    setAdvancedSearchModal(advancedSearchModalData);
  };
  const acceptModalData = (bookLoadData: any) => {
    let acceptModalMessage = bookLoadData?.message || '';
    acceptModalMessage = acceptModalMessage.replace(/\. /g, '.<br />');
    const formatMessage = (message: string) => {
      return message.split('<br />').map((part, index) => (
        <span key={index}>
          {part}
          {index < message.split('<br />').length - 1 && <br />}
        </span>
      ));
    };

    const currentDateTime = dayjs().format(
      `ddd, MMM DD, YYYY / ${getTimeFormatAccordingToPreference(true)}`,
    );
    const currentTimeZone = dayjs()
      .format('zzz')
      ?.split(' ')
      ?.map((word: string) => word[0])
      ?.join('');

    const updatedMemberDetails = memberDetails.map((item) => {
      if (item.label === 'Current Date / Time') {
        return {
          ...item,
          value: `${currentDateTime} ${currentTimeZone}`,
        };
      }
      return item;
    });

    if (bookLoadData?.status === 'SUCCESS') {
      let replacedAcceptModalMessage = acceptModalMessage?.replace(/\$1/g, data?.loadId);
      fbLogEvent(EVENT.UE_BOOK_NOW_SUCCESS, {
        screen: SCREEN.LOAD_DETAILS_RATE,
        lead: logEventLeadFinding(),
        ...(data?.division && { load_divison: data?.division }),
        ...(data?.rate?.binSources && { load_source_tms: data?.rate?.binSource }),
        ...(data?.rate?.binRateIdentifier && {
          load_bin_rate_id: data?.rate?.binRateIdentifier,
        }),
        ...(data?.estimatedTotalRpm && { load_estimated_rpm: data?.estimatedTotalRpm }),
      });
      fbLogEvent(EVENT.SV_LOAD_BOOK_SUCCESS, {
        screen: SCREEN.LOAD_BOOK_THANK_YOU,
        lead: logEventLeadFinding(),
        load_id: data?.loadId,
        ...(data?.division && { load_divison: data?.division }),
        ...(data?.rate?.binSources && { load_source_tms: data?.rate?.binSource }),
        ...(data?.rate?.binRateIdentifier && {
          load_bin_rate_id: data?.rate?.binRateIdentifier,
        }),
        ...(data?.estimatedTotalRpm && { load_estimated_rpm: data?.estimatedTotalRpm }),
      });
      if (selectedAnchor.current) {
        selectedAnchor.current = '';
      }
      return {
        title: 'Thank you',
        icon: '/oo/assets/icon/confirm.svg',
        children: (
          <div className="AcceptSuccessFailureModal">
            <div className="MemberDetails">
              {updatedMemberDetails?.map((item, index) => (
                <div key={index}>
                  {item.label !== '' && item.value !== '' && (
                    <div className="Detail_Item">
                      <div className="MemberDetails_Heading">{item.label} :</div>
                      <div className="MemberDetails_Details">{item.value}</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="AcceptModalMessage">{formatMessage(replacedAcceptModalMessage)}</div>
          </div>
        ),
        button: [
          {
            title: 'Go Home',
            type: 'secondary-cta-btn',
            onClick: () => {
              navigate('/', { state: { isReload: true } });
              fbLogEvent(EVENT.UE_LOAD_BOOK_GO_HOME, {
                screen: SCREEN.LOAD_BOOK_THANK_YOU,
                lead: logEventLeadFinding(),
                load_id: data?.loadId,
                ...(data?.division && { load_divison: data?.division }),
                ...(data?.rate?.binSources && { load_source_tms: data?.rate?.binSource }),
                ...(data?.rate?.binRateIdentifier && {
                  load_bin_rate_id: data?.rate?.binRateIdentifier,
                }),
                ...(data?.estimatedTotalRpm && { load_estimated_rpm: data?.estimatedTotalRpm }),
              });
            },
          },
          {
            title: 'Reload Now',
            type: 'primary-cta-btn',
            onClick: () => {
              setThankYouModalProps({ isModalOpen: false });
              const load =
                data?.loadStop?.find((item: any) => item?.stopSequence === data?.numberOfStops) ||
                data?.shipmentStops?.find((item: any) => item?.sequenceNo === data?.numberOfStops);
              const payload = {
                originLocation: {
                  City: load?.city,
                  State: load?.state,
                  Country: load?.country,
                  Zip: load?.postalCode,
                },
                destinationLocation: {},
                originRadius: appConfigData?.ORIGIN_RADIUS_INIT?.initialValue,
                destinationRadius: appConfigData?.DESTINATION_RADIUS_INIT?.initialValue,
                originFromDate: commonDateFormat(
                  isBeforeCurrentDate(load?.appointmentFrom || load?.plannedArrivalDateTime)
                    ? dayjs()
                    : load?.appointmentFrom || load?.plannedArrivalDateTime,
                ) as string,
                originToDate: commonDateFormat(
                  isBeforeCurrentDate(load?.appointmentTo || load?.plannedDepartureDateTime)
                    ? dayjs().add(pickupDateOffset, 'day')
                    : (load?.appointmentTo || load?.plannedDepartureDateTime) &&
                        dayjs(load?.appointmentTo || load?.plannedDepartureDateTime).add(
                          pickupDateOffset,
                          'day',
                        ),
                ) as string,
                destinationFromDate: commonDateFormat(dayjs()) as string,
                destinationToDate: commonDateFormat(dayjs().add(dropDateOffset, 'day')) as string,
              };
              dispatch(updateSearchQuery(payload));
              dispatch(handleResetSearchQueryError());
              navigate('/search', { state: { isReload: true } });
              fbLogEvent(EVENT.UE_LOAD_BOOK_RELOAD_NOW, {
                screen: SCREEN.LOAD_BOOK_THANK_YOU,
                lead: logEventLeadFinding(),
                load_id: data?.loadId,
                ...(data?.division && { load_divison: data?.division }),
                ...(data?.rate?.binSources && { load_source_tms: data?.rate?.binSource }),
                ...(data?.rate?.binRateIdentifier && {
                  load_bin_rate_id: data?.rate?.binRateIdentifier,
                }),
                ...(data?.estimatedTotalRpm && { load_estimated_rpm: data?.estimatedTotalRpm }),
              });
            },
          },
        ],
      };
    } else {
      fbLogEvent(EVENT.UE_BOOK_NOW_FAILURE, {
        screen: SCREEN.LOAD_DETAILS_RATE,
        lead: logEventLeadFinding(),
        ...(data?.division && { load_divison: data?.division }),
        ...(data?.rate?.binSources && { load_source_tms: data?.rate?.binSource }),
        ...(data?.rate?.binRateIdentifier && {
          load_bin_rate_id: data?.rate?.binRateIdentifier,
        }),
        ...(data?.estimatedTotalRpm && { load_estimated_rpm: data?.estimatedTotalRpm }),
      });
      fbLogEvent(EVENT.SV_LOAD_BOOK_FAIL, {
        screen: SCREEN.LOAD_BOOK_FAIL,
        lead: logEventLeadFinding(),
        AssociatedLoadId: data?.loadId,
        modalTitle: bookLoadData?.modalHeader || BookingFailed,
        failureReason: acceptModalMessage,
        ...(data?.division && { load_divison: data?.division }),
        ...(data?.rate?.binSources && { load_source_tms: data?.rate?.binSource }),
        ...(data?.rate?.binRateIdentifier && {
          load_bin_rate_id: data?.rate?.binRateIdentifier,
        }),
        ...(data?.estimatedTotalRpm && {
          load_estimated_rpm: data?.estimatedTotalRpm,
        }),
      });
      switch (bookLoadData?.reference) {
        case 'NewSearch':
          return {
            title: bookLoadData?.modalHeader || BookingFailed,
            icon: '/oo/assets/icon/info.svg',
            children: (
              <div className="AcceptSuccessFailureModal">
                <div className="MemberDetails">
                  {updatedMemberDetails?.map((item, index) => (
                    <div key={index}>
                      {item.label !== '' && item.value !== '' && (
                        <div className="Detail_Item">
                          <div className="MemberDetails_Heading">{item.label} :</div>
                          <div className="MemberDetails_Details">{item.value}</div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="AcceptModalMessage">{formatMessage(acceptModalMessage)}</div>
              </div>
            ),
            button: [
              {
                title: CANCEL_TEXT,
                type: 'secondary-cta-btn',
                onClick: () => {
                  fbLogEvent(EVENT.UE_LOAD_BOOK_DISMISS, {
                    screen: SCREEN.LOAD_BOOK_FAIL,
                    lead: logEventLeadFinding(),
                    AssociatedLoadId: data?.loadId,
                    modalTitle: BookingFailed,
                    failureReason: acceptModalMessage,
                    ...(data?.division && { load_divison: data?.division }),
                    ...(data?.rate?.binSources && { load_source_tms: data?.rate?.binSource }),
                    ...(data?.rate?.binRateIdentifier && {
                      load_bin_rate_id: data?.rate?.binRateIdentifier,
                    }),
                    ...(data?.estimatedTotalRpm && {
                      load_estimated_rpm: data?.estimatedTotalRpm,
                    }),
                  });
                  if (selectedAnchor.current !== null) {
                    selectedAnchor.current = '';
                  }
                  setThankYouModalProps({ isModalOpen: false });
                },
              },
              {
                title: RefreshResults,
                type: 'primary-cta-btn',
                onClick: () => {
                  fbLogEvent(
                    selectedAnchor?.current !== null
                      ? EVENT.UE_LOAD_BOOK_INSERT_SHIPMENT
                      : EVENT.UE_LOAD_BOOK_REFRESH_RESULTS,
                    {
                      screen: SCREEN.LOAD_BOOK_FAIL,
                      lead: logEventLeadFinding(),
                      AssociatedLoadId: data?.loadId,
                      modalTitle: BookingFailed,
                      failureReason: acceptModalMessage,
                      ...(data?.division && { load_divison: data?.division }),
                      ...(data?.rate?.binSources && { load_source_tms: data?.rate?.binSource }),
                      ...(data?.rate?.binRateIdentifier && {
                        load_bin_rate_id: data?.rate?.binRateIdentifier,
                      }),
                      ...(data?.estimatedTotalRpm && {
                        load_estimated_rpm: data?.estimatedTotalRpm,
                      }),
                    },
                  );
                  fbLogEvent(EVENT.UE_LOAD_BOOK_DISMISS, {
                    screen: SCREEN.LOAD_BOOK_FAIL,
                    lead: logEventLeadFinding(),
                    AssociatedLoadId: data?.loadId,
                    modalTitle: BookingFailed,
                    failureReason: acceptModalMessage,
                    ...(data?.division && { load_divison: data?.division }),
                    ...(data?.rate?.binSources && { load_source_tms: data?.rate?.binSource }),
                    ...(data?.rate?.binRateIdentifier && {
                      load_bin_rate_id: data?.rate?.binRateIdentifier,
                    }),
                    ...(data?.estimatedTotalRpm && {
                      load_estimated_rpm: data?.estimatedTotalRpm,
                    }),
                  });
                  if (selectedAnchor.current !== null) {
                    selectedAnchor.current = '';
                  }
                  const payload: any = {
                    size: sortAndFilter?.size,
                    page: sortAndFilter?.page,
                    sortColumn: sortAndFilter?.sortColumn,
                    sortDirection: sortAndFilter?.sortDirection,
                    originCity: searchQuery?.originLocation?.City ?? ANYWHERE,
                    originState: searchQuery?.originLocation?.State ?? '',
                    originRadius: searchQuery?.originRadius,
                    originCountry: searchQuery?.originLocation?.Country ?? 'US',
                    originPostalCode: searchQuery?.originLocation?.Zip ?? '',
                    pickUpStartDate:
                      dataBaseFriendlyDateFormat(searchQuery?.originFromDate, 'startDate', 'GMT') ??
                      null,
                    pickUpEndDate:
                      dataBaseFriendlyDateFormat(searchQuery?.originToDate, 'endDate', 'GMT') ??
                      null,
                    dropStartDate:
                      dataBaseFriendlyDateFormat(
                        searchQuery?.destinationFromDate,
                        'startDate',
                        'GMT',
                      ) || '',
                    dropEndDate:
                      dataBaseFriendlyDateFormat(
                        searchQuery?.destinationToDate,
                        'endDate',
                        'GMT',
                      ) || '',
                    destinationCity: searchQuery?.destinationLocation?.City ?? ANYWHERE,
                    destinationStates: Array.isArray(searchQuery?.destinationLocation?.State)
                      ? searchQuery?.destinationLocation?.State?.filter((item: any) => !!item) || []
                      : [searchQuery?.destinationLocation?.State],
                    destinationCountry: searchQuery?.destinationLocation?.Country ?? '',
                    destinationPostalCode: searchQuery?.destinationLocation?.Zip ?? '',
                    destinationRadius: searchQuery?.destinationRadius,
                    appointmentConfirmed: null,
                    extendedNetwork: null,
                  };
                  dispatch(getSearchLoads({ queryPayloads: payload }));
                  setThankYouModalProps({ isModalOpen: false });
                },
              },
            ],
          };
        case 'NATNAL':
          return {
            title: bookLoadData?.modalHeader || BookingFailed,
            icon: '/oo/assets/icon/info.svg',
            children: (
              <div className="AcceptSuccessFailureModal">
                <div className="MemberDetails">
                  {updatedMemberDetails?.map((item, index) => (
                    <div key={index}>
                      {item.label !== '' && item.value !== '' && (
                        <div className="Detail_Item">
                          <div className="MemberDetails_Heading">{item.label} :</div>
                          <div className="MemberDetails_Details">{item.value}</div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="AcceptModalMessage">{formatMessage(acceptModalMessage)}</div>
              </div>
            ),
            button: [
              {
                title: CANCEL_TEXT,
                type: 'secondary-cta-btn',
                onClick: () => {
                  fbLogEvent(EVENT.UE_LOAD_BOOK_DISMISS, {
                    screen: SCREEN.LOAD_BOOK_FAIL,
                    lead: logEventLeadFinding(),
                    AssociatedLoadId: data?.loadId,
                    modalTitle: BookingFailed,
                    failureReason: acceptModalMessage,
                    ...(data?.division && { load_divison: data?.division }),
                    ...(data?.rate?.binSources && { load_source_tms: data?.rate?.binSource }),
                    ...(data?.rate?.binRateIdentifier && {
                      load_bin_rate_id: data?.rate?.binRateIdentifier,
                    }),
                    ...(data?.estimatedTotalRpm && {
                      load_estimated_rpm: data?.estimatedTotalRpm,
                    }),
                  });
                  setThankYouModalProps({ isModalOpen: false });
                },
              },
              {
                title: UpdateNow,
                type: 'primary-cta-btn',
                onClick: () => {
                  fbLogEvent(EVENT.UE_LOAD_BOOK_UPDATE_NOW, {
                    screen: SCREEN.LOAD_BOOK_FAIL,
                    lead: logEventLeadFinding(),
                    AssociatedLoadId: data?.loadId,
                    modalTitle: BookingFailed,
                    failureReason: acceptModalMessage,
                    ...(data?.division && { load_divison: data?.division }),
                    ...(data?.rate?.binSources && { load_source_tms: data?.rate?.binSource }),
                    ...(data?.rate?.binRateIdentifier && {
                      load_bin_rate_id: data?.rate?.binRateIdentifier,
                    }),
                    ...(data?.estimatedTotalRpm && {
                      load_estimated_rpm: data?.estimatedTotalRpm,
                    }),
                  });
                  fbLogEvent(EVENT.UE_LOAD_BOOK_DISMISS, {
                    screen: SCREEN.LOAD_BOOK_FAIL,
                    lead: logEventLeadFinding(),
                    AssociatedLoadId: data?.loadId,
                    modalTitle: BookingFailed,
                    failureReason: acceptModalMessage,
                    ...(data?.division && { load_divison: data?.division }),
                    ...(data?.rate?.binSources && { load_source_tms: data?.rate?.binSource }),
                    ...(data?.rate?.binRateIdentifier && {
                      load_bin_rate_id: data?.rate?.binRateIdentifier,
                    }),
                    ...(data?.estimatedTotalRpm && {
                      load_estimated_rpm: data?.estimatedTotalRpm,
                    }),
                  });
                  setThankYouModalProps({ isModalOpen: false });
                  navigate('/natnal');
                },
              },
            ],
          };
        default:
          return {
            title: bookLoadData?.modalHeader || BookingFailed,
            icon: '/oo/assets/icon/info.svg',
            children: (
              <div className="AcceptSuccessFailureModal">
                <div className="MemberDetails">
                  {updatedMemberDetails?.map((item, index) => (
                    <div key={index}>
                      {item.label !== '' && item.value !== '' && (
                        <div className="Detail_Item">
                          <div className="MemberDetails_Heading">{item.label} :</div>
                          <div className="MemberDetails_Details">{item.value}</div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="AcceptModalMessage">
                  {formatMessage(acceptModalMessage) || SERVER_ERROR_MESSAGES?.generalMsg}
                </div>
              </div>
            ),
            button: [
              {
                title: OK_TEXT,
                type: 'primary-cta-btn',
                onClick: () => {
                  setThankYouModalProps({ isModalOpen: false });
                },
              },
            ],
          };
      }
    }
  };
  const footerComponent = () => {
    return (
      <p data-testid="Footer_Component">
        Click to{' '}
        <span
          className="underline CP"
          onClick={() => {
            fbLogEvent(EVENT.UE_LOAD_RATE_INSERT_SHIPMENT, {
              screen: SCREEN.LOAD_DETAILS_RATE,
              lead: logEventLeadFinding(),
              ...(data?.division && { load_divison: data?.division }),
              ...(data?.rate?.binSources && { load_source_tms: data?.rate?.binSource }),
              ...(data?.rate?.binRateIdentifier && {
                load_bin_rate_id: data?.rate?.binRateIdentifier,
              }),
              ...(data?.estimatedTotalRpm && { load_estimated_rpm: data?.estimatedTotalRpm }),
            });
            const callback = (res: any) => {
              setBookNowModalProps({ isModalOpen: false });
              const modalProps = {
                isModalOpen: true,
                title: `Load Sequence`,
                titleIcon: '/oo/assets/icon/info.svg',
                contentAlignment: 'left',
                additionalCssClass: 'InsertShipmentModal action-button-container contents button',
                buttons: [
                  {
                    title: CANCEL_TEXT,
                    type: 'secondary-cta-btn',
                    onClick: () => {
                      fbLogEvent(EVENT.UE_LOAD_SEQUENCE_CANCEL, {
                        screen: SCREEN.LOAD_DETAILS_LOAD_SEQUENCE,
                        lead: logEventLeadFinding(),
                        ...(data?.division && { load_divison: data?.division }),
                        ...(data?.rate?.binSources && { load_source_tms: data?.rate?.binSource }),
                        ...(data?.rate?.binRateIdentifier && {
                          load_bin_rate_id: data?.rate?.binRateIdentifier,
                        }),
                        ...(data?.estimatedTotalRpm && {
                          load_estimated_rpm: data?.estimatedTotalRpm,
                        }),
                      });
                      selectedAnchor.current = null;
                      handleShipmentSelection(false);
                      setInsertShipmentModalProps({ isModalOpen: false });
                      proceedWithBooking();
                    },
                  },
                  {
                    title: 'Accept',
                    type: `primary-cta-btn`,
                    isDisabled: !isShipmentSelected,
                    onClick: () => {
                      fbLogEvent(EVENT.UE_LOAD_SEQUENCE_ACCEPT, {
                        screen: SCREEN.LOAD_DETAILS_LOAD_SEQUENCE,
                        insertedShipmentId: selectedAnchor?.current,
                        lead: logEventLeadFinding(),
                        ...(data?.division && { load_divison: data?.division }),
                        ...(data?.rate?.binSources && { load_source_tms: data?.rate?.binSource }),
                        ...(data?.rate?.binRateIdentifier && {
                          load_bin_rate_id: data?.rate?.binRateIdentifier,
                        }),
                        ...(data?.estimatedTotalRpm && {
                          load_estimated_rpm: data?.estimatedTotalRpm,
                        }),
                      });
                      let _payload: any = { ...payload };
                      if (selectedAnchor?.current) {
                        _payload = {
                          ...payload,
                          anchorShipmentID: selectedAnchor?.current,
                          anchorDirection: 'ASSIGN DRIVER AFTER',
                        };
                      }
                      dispatch(bookLoad(_payload)).then((res) => {
                        setBookNowModalProps({ isModalOpen: false });
                        handleShipmentSelection(false);
                        setInsertShipmentModalProps({ isModalOpen: false });
                        handleAcceptClick(res.payload);
                      });
                    },
                  },
                ],
              };
              setInsertShipmentModalProps({ ...modalProps });
              fbLogEvent(EVENT.SV_VIEW_BOOK_NOW_LOAD_SEQUENCE, {
                screen: SCREEN.LOAD_DETAILS_LOAD_SEQUENCE,
                loadId: data?.loadId,
                lead: logEventLeadFinding(),
                ...(data?.division && { load_divison: data?.division }),
                ...(data?.rate?.binSources && { load_source_tms: data?.rate?.binSource }),
                ...(data?.rate?.binRateIdentifier && {
                  load_bin_rate_id: data?.rate?.binRateIdentifier,
                }),
                ...(data?.estimatedTotalRpm && { load_estimated_rpm: data?.estimatedTotalRpm }),
              });
            };
            dispatch(
              getAvailableAnchorShipments({
                payload: {
                  driverId:
                    memberData?.member?.userApplicationDetails?.[0].attributes
                      .PrimaryBookingDriverId,
                },
                callback: callback,
              }),
            );
          }}
        >
          Insert Shipment
        </span>
      </p>
    );
  };
  const proceedWithBooking = () => {
    fbLogEvent(EVENT.SV_VIEW_BOOK_NOW_RATE, {
      screen: SCREEN.LOAD_DETAILS_RATE,
      loadId: data?.loadId,
      lead: logEventLeadFinding(),
      ...(data?.division && { load_divison: data?.division }),
      ...(data?.rate?.binSources && { load_source_tms: data?.rate?.binSource }),
      ...(data?.rate?.binRateIdentifier && {
        load_bin_rate_id: data?.rate?.binRateIdentifier,
      }),
      ...(data?.estimatedTotalRpm && { load_estimated_rpm: data?.estimatedTotalRpm }),
    });
    const modalProps = {
      isModalOpen: true,
      title: `Rate Details of ${data?.loadId}`,
      titleIcon: '/oo/assets/icon/info.svg',
      contentAlignment: 'center',
      additionalCssClass:
        'BookNow_Modal bookNow-modal Table_Container action-button-container contents button',
      children: (
        <>
          <LoadRateContainer tableTitle={''} rate={data?.rate} isMemberDetailVisible={true} />
          {data?.extendedNetwork && (
            <div className="BookingModal_ExtendedNetwork_Indicator">Extended Network</div>
          )}
          <TermsAndConditions />
        </>
      ),
      buttons: [
        {
          title: 'Decline',
          type: 'secondary-cta-btn',
          onClick: () => {
            fbLogEvent(EVENT.UE_LOAD_RATE_DECLINE, {
              screen: SCREEN.LOAD_DETAILS_RATE,
              lead: logEventLeadFinding(),
              ...(data?.division && { load_divison: data?.division }),
              ...(data?.rate?.binSources && { load_source_tms: data?.rate?.binSource }),
              ...(data?.rate?.binRateIdentifier && {
                load_bin_rate_id: data?.rate?.binRateIdentifier,
              }),
              ...(data?.estimatedTotalRpm && { load_estimated_rpm: data?.estimatedTotalRpm }),
            });
            setBookNowModalProps({ isModalOpen: false });
          },
        },
        {
          title: 'Accept',
          type: 'primary-cta-btn',
          onClick: () => {
            fbLogEvent(EVENT.UE_LOAD_RATE_ACCEPT, {
              screen: SCREEN.LOAD_DETAILS_RATE,
              lead: logEventLeadFinding(),
              ...(data?.division && { load_divison: data?.division }),
              ...(data?.rate?.binSources && { load_source_tms: data?.rate?.binSource }),
              ...(data?.rate?.binRateIdentifier && {
                load_bin_rate_id: data?.rate?.binRateIdentifier,
              }),
              ...(data?.estimatedTotalRpm && { load_estimated_rpm: data?.estimatedTotalRpm }),
            });
            dispatch(bookLoad(payload)).then((res) => {
              selectedAnchor.current = null;
              setBookNowModalProps({ isModalOpen: false });
              handleAcceptClick(res.payload);
            });
          },
        },
      ],
      footer:
        memberData?.member?.userApplicationDetails?.[0].attributes.LineOfService !== 'BULK' &&
        !data?.extendedNetwork
          ? footerComponent()
          : null,
    };
    fbLogEvent(EVENT.UE_BOOK_LOAD_NOW, {
      screen: SCREEN.SEARCH,
      tab: logEventLeadFinding(),
      loadId: data?.loadId,
      ...(data?.division && { load_divison: data?.division }),
      ...(data?.rate?.binSources && { load_source_tms: data?.rate?.binSource }),
      ...(data?.rate?.binRateIdentifier && {
        load_bin_rate_id: data?.rate?.binRateIdentifier,
      }),
      ...(data?.estimatedTotalRpm && { load_estimated_rpm: data?.estimatedTotalRpm }),
    });
    setBookNowModalProps(modalProps);
  };
  const handleBookNowClick = () => {
    const originLoadStop =
      data?.loadStop?.find((item: any) => item?.stopSequence === 1) ||
      data?.shipmentStops?.find((item: any) => item?.sequenceNo === 1);
    const originAppointmentFrom = commonDateFormat(
      originLoadStop?.appointmentFrom || originLoadStop?.plannedArrivalDateTime,
      false,
      false,
      originLoadStop?.timeZone ? originLoadStop?.timeZone : null,
    );
    const isToday = dayjs(originAppointmentFrom).isSame(dayjs(), 'day');
    if (isToday) {
      const temp = {
        isModalOpen: true,
        title: `Warning`,
        titleIcon: '/oo/assets/icon/alertInTriangle.svg',
        contentAlignment: 'center',
        message:
          "<div class='Warning_Modal_Text'><h3 class='Warning_Modal_Regular' >This route has the following warnings. Please confirm that you move the load by clicking Accept. </h3><div><h2 class='Warning_Modal_Medium'>This route picks up today.</h2></div></div>",
        additionalCssClass: 'additional-css',
        buttons: [
          {
            title: CANCEL_TEXT,
            type: 'secondary-cta-btn',
            onClick: () => {
              setWarningModalProps({ isModalOpen: false });
            },
          },
          {
            title: 'Accept',
            type: 'primary-cta-btn',
            onClick: () => {
              setWarningModalProps({ isModalOpen: false });
              proceedWithBooking();
            },
          },
        ],
      };
      setWarningModalProps(temp);
    } else {
      proceedWithBooking();
    }
  };
  const handleAcceptClick = (bookLoadData: any) => {
    const modalData = acceptModalData(bookLoadData);
    if (modalData) {
      const modalProps = {
        isModalOpen: true,
        title: modalData?.title,
        titleIcon: modalData?.icon,
        children: modalData?.children,
        buttons: modalData?.button,
        contentScroll: false,
        additionalCssClass: 'Thankyou_Modal',
      };
      setThankYouModalProps(modalProps);
    } else {
      toast('Something went wrong');
    }
  };
  const handleLastShipmentModal = () => {
    const modalProps = {
      isModalOpen: true,
      title: lastLocation?.shipmentId,
      titleIcon: '/oo/assets/icon/info.svg',
      size: 'small-height',
      message: `<div class='my-3 px-2 py-2'><span>${LAST_SHIPMENT_MODAL_MESSAGE}</span ></div>`,
      buttons: [
        {
          title: OK_TEXT,
          type: 'primary-cta-btn',
          onClick: () => {
            setLastShipmentWarningModal({ isModalOpen: false });
          },
        },
      ],
    };
    setLastShipmentWarningModal(modalProps);
  };

  const currencyFormatter = (currency: string, costAmount: any) => {
    const currencyFormatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 2,
    });
    const formattedCostAmount = currencyFormatter.format(costAmount);
    return formattedCostAmount;
  };

  const hasAdvancedData =
    data?.minRPM ||
    (data?.minMiles !== null && data?.maxMiles !== null) ||
    data?.excludeSpecialServices?.length > 0;

  const hasViewMoreData = data?.excludeSpecialServices?.length > 0;

  const CenterPartSection = ({
    imgSrc,
    type,
    city,
    state,
    radius,
    startDate,
    endDate,
    stopInfo,
    showReload,
    showRate,
    handleReload,
  }: any) => {
    const [locationDetail, setLocationDetail] = useState(false);
    const [newStartDate, setNewStartDate] = useState(startDate);
    const [newEndDate, setNewEndDate] = useState(endDate);
    const updateLocationDetail = () => {
      setLocationDetail(!locationDetail);
    };
    useEffect(() => {
      if (startDate && endDate && isResultsCard) {
        const dates = getNewDates(startDate, endDate, type);
        setNewStartDate(dates.newStartDate);
        setNewEndDate(dates.newEndDate);
      }
    }, [startDate, endDate]);

    return (
      <div className="Center_Part_Section">
        <div className="Location_City_State">
          <div className="Location_Main_Information">
            <img src={imgSrc} alt={`${type}_Icon`} />
            <span>
              {city ? `${city}${state ? ',' : ''}` : ''} {state && ` ${state}`}
            </span>
            {isExpanded && (
              <img
                className={`Expand_Icon ${locationDetail ? 'Minus' : 'Plus'}`}
                src={`/oo/assets/icon/${locationDetail ? 'minus-icon' : 'plus'}.svg`}
                alt="More Info"
                onClick={updateLocationDetail}
              />
            )}
            {!isMobile && showReload && (
              <ReloadButton
                isExpandedCard={isExpanded}
                isRecentlyDelivered={isRecentlyDelivered}
                handleClick={() => {
                  fbLogEvent(EVENT.UE_SEARCH_RELOAD, {
                    screen: SCREEN.SEARCH,
                    tab: logEventLeadFinding(),
                    loadId: data?.loadId,
                    ...(data?.division && { load_divison: data?.division }),
                    ...(data?.rate?.binSources && { load_source_tms: data?.rate?.binSource }),
                    ...(data?.rate?.binRateIdentifier && {
                      load_bin_rate_id: data?.rate?.binRateIdentifier,
                    }),
                    ...(data?.estimatedTotalRpm && { load_estimated_rpm: data?.estimatedTotalRpm }),
                  });
                  const originStop =
                    data?.loadStop?.find((item: any) => item?.stopSequence === '1') ||
                    data?.shipmentStops?.find((item: any) => item?.sequenceNo === '1');
                  const destinationStop =
                    data?.loadStop?.find(
                      (item: any) => item?.stopSequence === data?.numberOfStops,
                    ) ||
                    data?.shipmentStops?.find(
                      (item: any) => item?.sequenceNo === data?.numberOfStops,
                    );
                  const payload = {
                    originLocation: isRecentlyDelivered
                      ? {
                          City: originStop?.city,
                          State: originStop?.state,
                          Country: originStop?.country,
                          Zip: originStop?.postalCode,
                        }
                      : {
                          City: destinationStop?.city,
                          State: destinationStop?.state,
                          Country: destinationStop?.country,
                          Zip: destinationStop?.postalCode,
                        },
                    destinationLocation: isRecentlyDelivered
                      ? {
                          City: destinationStop?.city,
                          State: destinationStop?.state,
                          Country: destinationStop?.country,
                          Zip: destinationStop?.postalCode,
                        }
                      : {},
                    originRadius: appConfigData?.ORIGIN_RADIUS_INIT?.initialValue,
                    destinationRadius: appConfigData?.DESTINATION_RADIUS_INIT?.initialValue,
                    originFromDate: commonDateFormat(
                      isBeforeCurrentDate(
                        isRecentlyDelivered
                          ? originStop?.appointmentFrom || originStop?.plannedArrivalDateTime
                          : destinationStop?.appointmentFrom ||
                              destinationStop?.plannedArrivalDateTime,
                      )
                        ? dayjs()
                        : isRecentlyDelivered
                          ? originStop?.appointmentFrom || originStop?.plannedArrivalDateTime
                          : destinationStop?.appointmentFrom ||
                            destinationStop?.plannedArrivalDateTime,
                    ) as string,
                    originToDate: commonDateFormat(
                      isBeforeCurrentDate(
                        isRecentlyDelivered
                          ? originStop?.appointmentTo || originStop?.plannedDepartureDateTime
                          : destinationStop?.appointmentTo ||
                              destinationStop?.plannedDepartureDateTime,
                      )
                        ? dayjs().add(pickupDateOffset, 'day')
                        : (isRecentlyDelivered
                            ? originStop?.appointmentTo || originStop?.plannedDepartureDateTime
                            : destinationStop?.appointmentTo ||
                              destinationStop?.plannedDepartureDateTime) &&
                            dayjs(
                              isRecentlyDelivered
                                ? originStop?.appointmentTo || originStop?.plannedDepartureDateTime
                                : destinationStop?.appointmentTo ||
                                    destinationStop?.plannedDepartureDateTime,
                            ).add(pickupDateOffset, 'day'),
                    ) as string,
                    destinationFromDate: commonDateFormat(
                      isBeforeCurrentDate(
                        destinationStop?.appointmentFrom || destinationStop?.plannedArrivalDateTime,
                      )
                        ? dayjs()
                        : destinationStop?.appointmentFrom ||
                            destinationStop?.plannedArrivalDateTime,
                    ) as string,
                    destinationToDate: commonDateFormat(
                      isBeforeCurrentDate(
                        destinationStop?.appointmentTo || destinationStop?.plannedDepartureDateTime,
                      )
                        ? dayjs().add(dropDateOffset, 'day')
                        : (destinationStop?.appointmentTo ||
                            destinationStop?.plannedDepartureDateTime) &&
                            dayjs(
                              destinationStop?.appointmentTo ||
                                destinationStop?.plannedDepartureDateTime,
                            ).add(dropDateOffset, 'day'),
                    ) as string,
                  };
                  dispatch(updateSearchQuery(payload));
                  dispatch(handleResetSearchQueryError());
                  navigate('/search', { state: { isReload: true } });
                }}
              />
            )}
          </div>
          <div className="Shipment_Information">
            {isMobile && locationDetail && (
              <div className="Location_Details_Container">
                <p>{stopInfo?.locationName}</p>
                <p>{stopInfo?.addressLine1}</p>
                <p>
                  {' '}
                  {stopInfo.city},{stopInfo.state},{stopInfo.postalCode}
                </p>
              </div>
            )}
            {commonDateFormat(stopInfo?.appointmentFrom || stopInfo?.plannedArrivalDateTime) &&
              commonDateFormat(stopInfo?.appointmentTo || stopInfo?.plannedDepartureDateTime) && (
                <div className={`${type} Appointment_Date`}>
                  <p className="From Date">
                    {commonDateFormat(
                      stopInfo?.appointmentFrom || stopInfo?.plannedArrivalDateTime,
                      false,
                      true,
                      stopInfo?.timeZone ? stopInfo?.timeZone : null,
                    )}
                    {' - '}
                    <br />
                    {commonDateFormat(
                      stopInfo?.appointmentTo || stopInfo?.plannedDepartureDateTime,
                      false,
                      true,
                      stopInfo?.timeZone ? stopInfo?.timeZone : null,
                    )}
                  </p>
                </div>
              )}
            {!isMyLoads
              ? stopInfo?.specialServices?.length > 0 &&
                !hideSpecialServices && (
                  <div className="Stop_Special_Services">
                    {stopInfo?.specialServices?.map((item: any, index: number) => (
                      <span key={item?.specialServiceDesc + index}>
                        <span
                          style={
                            appConfigData?.SPECIAL_STOP_ACTIONS_INIT?.[item?.specialServiceDesc] ||
                            null
                          }
                        >
                          {item?.specialServiceDesc}
                          <br />
                        </span>
                      </span>
                    ))}
                  </div>
                )
              : stopInfo?.stopActivity?.length > 0 && (
                  <div className="Stop_Special_Services">
                    {stopInfo?.stopActivity?.map((item: any, index: number) => (
                      <span key={item?.tmsActivity + index}>
                        <span
                          style={
                            appConfigData?.SPECIAL_STOP_ACTIONS_INIT?.[item?.tmsActivity] || null
                          }
                        >
                          {item?.tmsActivity}
                          <br />
                        </span>
                      </span>
                    ))}
                  </div>
                )}
            {isMyLoads && isMobile && isExpanded && locationDetail && (
              <div className="StopActuals">
                <div className="StopActualsTitle">Arrived :</div>
                <div className="StopActualsValue">
                  {commonDateFormat(
                    stopInfo?.actualArrivalDateTime,
                    false,
                    true,
                    stopInfo?.timeZone ? stopInfo?.timeZone : null,
                  )}
                </div>
                <div className="StopActualsTitle">Trailer In :</div>
                <div className="StopActualsValue">
                  {stopInfo?.stopActivity?.map(
                    (item: any, index: number) =>
                      item.execActivity === 'TRAILER_IN' && (
                        <span key={item?.equipmentId + index}>{item?.equipmentId}</span>
                      ),
                  )}
                </div>
                <div className="StopActualsDepartedTitle">Departed :</div>
                <div className="StopActualsValue">
                  {commonDateFormat(
                    stopInfo?.actualDepartureDateTime,
                    false,
                    true,
                    stopInfo?.timeZone ? stopInfo?.timeZone : null,
                  )}
                </div>
                <div className="StopActualsTitle">Trailer Out :</div>
                <div className="StopActualsValue">
                  {stopInfo?.stopActivity?.map(
                    (item: any, index: number) =>
                      item.execActivity === 'TRAILER_OUT' && (
                        <span key={item?.equipmentId + index}>{item?.equipmentId}</span>
                      ),
                  )}
                </div>
              </div>
            )}
            {deadHeadValidation(stopInfo?.deadHead) && !hideDeadHead && (
              <div className="Dead_Head">
                <p>Empty {roundToNearestWholeNumber(stopInfo?.deadHead)} mi</p>
              </div>
            )}
            {stopInfo?.timeZone && isMobile && isExpanded && (
              <div className="Time_Zone">Time Zone: {stopInfo?.timeZone}</div>
            )}
            {showRate && (
              <div className="Total_Rate_Label">
                <div className="Price_Label">Rate:</div>
                <div className="Price_Amount">
                  {currencyFormatter(data?.costCurrency, data?.totalCostAmount)}
                </div>
              </div>
            )}
          </div>
        </div>
        {!isMyLoads && !isInsertShipment && (
          <div className={`Information ${type} ${isInsertShipment ? 'InsertShipment' : ''}`}>
            {radius && (
              <div className={`${type}_Radius`}>
                {isMobile ? `Radius : ${radius} mi` : `${radius} miles`}
              </div>
            )}
            {!showDateTimeRightSection &&
              (isMobile ? (
                <>
                  {commonDateFormat(newStartDate, withoutDateTime, withoutYear) && (
                    <div className={`${type}_Start_Date`}>
                      {commonDateFormat(newStartDate, withoutDateTime, withoutYear)}{' '}
                      {commonDateFormat(newEndDate, withoutDateTime, withoutYear) && '-'}
                    </div>
                  )}
                  {commonDateFormat(newEndDate, withoutDateTime, withoutYear) && (
                    <div className={`${type}_End_Date`}>
                      {commonDateFormat(newEndDate, withoutDateTime, withoutYear)}
                    </div>
                  )}
                </>
              ) : (
                (commonDateFormat(newStartDate, withoutDateTime) ||
                  commonDateFormat(newEndDate, withoutDateTime)) && (
                  <div>
                    {commonDateFormat(newStartDate, withoutDateTime) && (
                      <span className={`${type}_Start_Date`}>
                        {commonDateFormat(newStartDate, withoutDateTime)}{' '}
                        {commonDateFormat(newEndDate, withoutDateTime) && '-'}{' '}
                      </span>
                    )}
                    {commonDateFormat(newEndDate, withoutDateTime) && (
                      <span className={`${type}_End_Date`}>
                        {commonDateFormat(newEndDate, withoutDateTime)}
                      </span>
                    )}
                  </div>
                )
              ))}
          </div>
        )}
      </div>
    );
  };

  const LeftPartSection = () => {
    return (
      <div className="AdvancedSearch_Container">
        <div className="Advanced_Data">
          {!isMobile ? <p data-testid="MinRatePerMile">Min Loaded RPM</p> : <p>Min Loaded RPM</p>}
          <span data-testid="MinRatePerMileValue">
            {data?.minRPM ? `$${data?.minRPM.toFixed(2)}` : 'No Min'}
          </span>
        </div>
        <div className="Advanced_Data">
          <p data-testid="LoadDistance">Load Distance</p>
          <div className="LoadDistance_Container">
            {isMobile && (
              <>
                <span>Min: {data?.minMiles}</span>
                <span>Max: {data?.maxMiles === 9999 ? '1000+' : data?.maxMiles}</span>
              </>
            )}
            {!isMobile && (
              <>
                <span data-testid="LoadDistanceMinValue">Minimum: {data?.minMiles}</span>

                <span data-testid="LoadDistanceMaxValue">
                  Maximum: {data?.maxMiles === 9999 ? '1000+' : data?.maxMiles}
                </span>
              </>
            )}
          </div>
        </div>
        {data?.excludeSpecialServices?.length > 0 && (
          <div className="Advanced_Data">
            <div className="Exclusions_Header">
              <p>Load Exclusions: </p>
              {!isMobile && (
                <div>
                  <img
                    src="/oo/assets/icon/info.svg"
                    alt="Info"
                    className="Alert_Tooltip_Icon"
                    onMouseEnter={() => {
                      setIsAdvancedTooltipActive(true);
                    }}
                    onMouseLeave={() => {
                      setIsAdvancedTooltipActive(false);
                    }}
                  />
                </div>
              )}
            </div>
            <div>
              <Tooltip
                appointmentClass={true}
                message={advanceSearchTooltip}
                isActive={!!advanceSearchTooltip}
                wrapText={true}
                additionalClass={'tooltip_advanced_search'}
              />
            </div>
            <div className="Exclusions_Container">
              {isMobile &&
                data?.excludeSpecialServices
                  ?.map((item: any) => item.toUpperCase())
                  ?.slice(0, showAllLoadExclusions ? data?.excludeSpecialServices.length : 2)
                  .map((item: any, index: number) => (
                    <span key={index}>
                      {index === 1 &&
                      !showAllLoadExclusions &&
                      data?.excludeSpecialServices.length > 2
                        ? `${item.split(' ')[0]}...`
                        : item}
                    </span>
                  ))}
              {!isMobile &&
                data?.excludeSpecialServices
                  ?.map((item: any) => item.toUpperCase())
                  ?.slice(0, showAllLoadExclusions ? data?.excludeSpecialServices.length : 3)
                  .map((item: any, index: number) => (
                    <span key={index}>
                      {index === 2 &&
                      !showAllLoadExclusions &&
                      data?.excludeSpecialServices.length > 3
                        ? `${item.split(' ')[0]}...`
                        : item}
                    </span>
                  ))}
            </div>
          </div>
        )}
        {isMobile && hasViewMoreData && (
          <div
            className="ViewMore_Advanced"
            onClick={() => {
              handleViewMoreAdvancedSearchClick();
            }}
            data-testid="ViewMore"
          >
            View More
          </div>
        )}
      </div>
    );
  };

  const logEventLeadFinding = () => {
    if (isRecent) {
      return TAB.RECENT;
    } else if (isSearchResults) {
      return TAB.NEW;
    } else if (isFavorite) {
      return TAB.FAVORITE;
    } else if (isWatchedLoads) {
      return TAB.WATCHED;
    }
  };

  const hasStopOffs = () => {
    let stopOffs;
    if (data?.numberOfStops > 2) {
      stopOffs = data?.numberOfStops - 2;
    }
    return stopOffs;
  };

  // const getPersonaName = () => {
  //   let name = '';
  //   let driverName = data?.shipmentInvolvedParties?.filter((data: any) => {
  //     return data.partyType === 'PRIMARY DRIVER';
  //   });
  //   driverName = driverName && driverName.length ? driverName[0].partyName : '';

  //   if (driverName) {
  //     const fName = driverName.slice(0, 1);
  //     const LName = driverName.includes(' ')
  //       ? driverName.slice(driverName.indexOf(' ') + 1, driverName.indexOf(' ') + 2)
  //       : '';
  //     name = fName.toUpperCase() + LName.toUpperCase();
  //   } else {
  //     name = 'U';
  //   }
  //   return name;
  // };

  return (
    <>
      <CustomModal {...advancedSearchModalProps} />
      <CustomModal {...bookNowModalProps} />
      <CustomModal {...thankYouModalProps} />
      <CustomModal {...warningModalProps} />
      <CustomModal {...lastShipmentWarningModal} />
      <CustomModal {...insertShipmentModalProps}>
        <div className="InsertShipmentModalContainer">
          <div className="NewLoadContainer">
            <p className="NewLoadContainerHeading">New Load</p>
            <LoadCard
              data={data}
              size={ELoadCardSize.medium}
              isRecent={isRecent}
              isFavorite={isFavorite}
              isSearchResults={isSearchResults}
              isWatchedLoads={isWatchedLoads}
              hideRightTopPart={true}
              hideSpecialServices={true}
              showRateLeft={true}
              hideRightPart={true}
              hideSpecialInfoLeft={true}
              hideDeadHead={true}
              additionalCssClass="InsertShipmentLoadCardPadding"
            />
          </div>
          <div className="NextShipmentModalContainer">
            <div className="NextShipmentModalContainerHeading">
              Select shipment new load comes after
            </div>
            <div className="NextShipmentContainer">
              {availableAnchorShipments.length > 0 &&
                availableAnchorShipments?.map((item: any, index?: number) => (
                  <LoadCard
                    key={item?.shipmentId}
                    data={item}
                    showDateTimeRightSection={true}
                    size={ELoadCardSize.medium}
                    isRecent={isRecent}
                    isFavorite={isFavorite}
                    isSearchResults={isSearchResults}
                    isWatchedLoads={isWatchedLoads}
                    cardClickEnabled={true}
                    additionalCssClass={`${
                      item?.shipmentId === selectedAnchor?.current ? 'anchorShipment' : ''
                    } ${'InsertShipmentLoadCardPadding'} ${isLastShipment(lastLocation, availableAnchorShipments) && item?.shipmentId === lastLocation?.shipmentId ? 'disabled' : ''}`}
                    handleCardClick={(id) => {
                      if (
                        isLastShipment(lastLocation, availableAnchorShipments) &&
                        item?.shipmentId === lastLocation?.shipmentId
                      ) {
                        selectedAnchor.current = null;
                        handleShipmentSelection(false);
                        handleLastShipmentModal();
                      } else {
                        if (id === selectedAnchor?.current) {
                          selectedAnchor.current = '';
                          handleShipmentSelection(false);
                        } else {
                          selectedAnchor.current = id || '';
                          handleShipmentSelection(true);
                        }
                      }
                      setInsertShipmentModalProps((prevProps: any) => ({
                        ...prevProps,
                        buttons: prevProps?.buttons?.map((btn: any) =>
                          btn?.title === 'Accept'
                            ? { ...btn, isDisabled: !selectedAnchor.current }
                            : btn,
                        ),
                      }));
                    }}
                    hideRightTopPart={true}
                    hideSpecialServices={true}
                    hideDeadHead={true}
                    hideRightPart={true}
                    hideSpecialInfoLeft={true}
                  />
                ))}
            </div>
          </div>
        </div>
      </CustomModal>
      <div
        className={`LoadCard ${size} ${
          dropDownIndicator === 'down' ? 'Unexpanded' : 'Expanded'
        } ${additionalCssClass ? additionalCssClass : ''}`}
        data-testid="LoadCard"
        id={data?.loadId || data?.shipmentId}
        onClick={(e) => {
          e.stopPropagation();
          const target = (e.target as HTMLElement).className;
          if (
            target !== 'Right_Part_Top' &&
            target !== 'Appointment_Indicator' &&
            target !== 'Appointment_Indicator_Icon' &&
            target !== 'ReloadButton' &&
            target !== 'Reload_Icon' &&
            target !== 'Reload_Label' &&
            target !== 'ViewMore_Advanced'
          ) {
            (isMobile || cardClickEnabled) &&
              handleCardClick &&
              handleCardClick(data?.id || data?.loadId || data?.shipmentId);
          }
        }}
      >
        <div className="LoadCard_Main">
          <div className={`LoadCard_Content ${data?.extendedNetwork && 'pb-0'}`}>
            <div className="Appointment_Tooltip_Container">
              <Tooltip
                appointmentClass={true}
                icon={tooltipIcon}
                message={tooltip}
                isActive={!!tooltip}
                wrapText={true}
              />
            </div>
            {!isAppointment && !isMyLoads && (isWatchedLoads || isSearchResults || isExpanded) && (
              <div className={`Appointment_Indicator`}>
                <img
                  onClick={() => setTooltip(APPOINTMENT_TOOLTIP_MESSAGE)}
                  onMouseEnter={() => setTooltip(APPOINTMENT_TOOLTIP_MESSAGE)}
                  onMouseLeave={() => setTooltip('')}
                  src="/oo/assets/icon/appointment-time-indicator.svg"
                  alt="Appointment Indicator"
                  className="Appointment_Indicator_Icon"
                />
              </div>
            )}
            <div
              className={`Left_Part ${!isAppointment && !isMyLoads ? 'Appt_Padding' : ''}  ${
                showAdvancedSearchData ? 'AdvancedData_Padding' : ''
              }`}
            >
              {isMobile && isMyLoads && <p className="LoadId">{data?.shipmentId}</p>}
              {(isWatchedLoads ||
                isMyLoads ||
                isSearchResults ||
                isExpanded ||
                isRecent ||
                isFavorite) && (
                <>
                  {isMobile && !showRateLeft && !hideSpecialInfoLeft && (
                    <p className="Total_Charges">
                      {data?.rate?.totalCharge && (
                        <>
                          $
                          {convertCurrencyFormat(
                            Math?.floor(data?.rate?.totalCharge) || data?.rate?.totalCharge,
                          )}
                        </>
                      )}
                    </p>
                  )}
                  {!isMobile && (data?.loadId || data?.shipmentId) && (
                    <p>{data?.loadId || data?.shipmentId}</p>
                  )}
                  {showLoadIdLeftMobile && isMobile && (data?.loadId || data?.shipmentId) && (
                    <p className="LoadId">{data?.loadId || data?.shipmentId}</p>
                  )}
                  {hideSpecialInfoLeft ? (
                    showRateLeft && (
                      <p className="Total_Charges_Left_Orientation">
                        Total Rate : $
                        {convertCurrencyFormat(
                          Math?.floor(data?.rate?.totalCharge) || data?.rate?.totalCharge,
                        )}
                      </p>
                    )
                  ) : (
                    <>
                      {convertCurrencyFormat(data?.totalDistance) && (
                        <p>{convertCurrencyFormat(data?.totalDistance)} miles</p>
                      )}
                      {isMobile && hasStopOffs() && <p>{hasStopOffs()} stop offs</p>}
                      <p>
                        {convertCurrencyFormat(data?.totalWeight)} {data?.weightUOM}
                      </p>
                      {!isMobile && hasStopOffs() && <p>{hasStopOffs()} stop offs</p>}
                      <p>{data?.isHazardous ? 'Hazmat' : ''}</p>
                      <p>{data?.isTemperatureControlled ? 'Temperature Control' : ''}</p>
                      {isMobile &&
                        (isWatchedLoads || isSearchResults || isExpanded) &&
                        data?.rate?.totalCharge &&
                        !showRateLeft && (
                          <div className="Load_RPM" data-testid="Load_RPM">
                            <span>Loaded RPM: </span>
                            <span data-testid="Load_RPM_Value">
                              {currencyFormatter(data?.costCurrency, data?.ratePerMile)}
                            </span>
                          </div>
                        )}
                      {isMobile &&
                        (isSearchResults || isRecent || isFavorite) &&
                        !(isWatchedLoads || isGlobalSearch || isMyLoads || showRateLeft) &&
                        isValidRPM(data?.estimatedTotalRpm) &&
                        searchQuery?.originLocation?.State?.length > 0 && (
                          <div className="Est_Total_RPM" data-testid="Est_Total_RPM">
                            <span>Est Total RPM: </span>
                            <span className="Est_Total_RPM_Value">
                              {currencyFormatter(data?.costCurrency, data?.estimatedTotalRpm)}
                            </span>
                          </div>
                        )}
                    </>
                  )}
                  {isMyLoads && isMobile && (
                    <div className="Total_Rate_Label">
                      <div className="Price_Label">Rate:</div>
                      <div className="Price_Amount">
                        {currencyFormatter(data?.costCurrency, data?.totalCostAmount)}
                      </div>
                    </div>
                  )}
                  {data?.sid && (
                    <div className={`ATL_Div ${isMobile ? 'small' : 'large'}`}>
                      <div className={`ATL_Label ${isMobile ? 'small' : 'large'}`}>
                        Alternate ID:
                      </div>
                      <div className={`ATL_ID ${isMobile ? 'small' : 'large'}`}>{data?.sid}</div>
                    </div>
                  )}
                  {/* {isMyLoads && (
                    <div className={`Driver_Persona_Div ${isMobile ? 'small' : 'large'}`}>
                      <div className="Persona ">
                        <span className="Persona_Name">{getPersonaName()}</span>
                      </div>
                    </div>
                  )} */}
                </>
              )}
              {showAdvancedSearchData && hasAdvancedData && <LeftPartSection />}
            </div>
            {showNewUpdateFlag && !isMyLoads && (data?.newLoad || data?.updateLoad) && (
              <span className="NewUpdateIcon">
                <img
                  src={`/oo/assets/icon/${data?.newLoad ? 'new' : data?.updateLoad ? 'update' : null}.svg
        `}
                  alt={`${data?.newLoad ? 'New' : 'Update'}_Icon`}
                />
              </span>
            )}
            <div
              className={`Center_Part ${showAdvancedSearchData ? 'no-padding' : ''} ${isMyLoads ? 'my-loads' : ''} ${showNewUpdateFlag && !isMyLoads && (data?.newLoad || data?.updateLoad) ? 'New_Update_Flag' : ''} `}
            >
              <div className="Center_Part_Section_Divs">
                {CenterPartSection({
                  type: 'origin',
                  imgSrc: '/oo/assets/icon/origin_icon.svg',
                  city:
                    data?.originCity ||
                    data?.loadStop?.find(
                      (item: any) => item?.stopSequence === 1 || item?.sequenceNo === 1,
                    )?.city ||
                    data?.shipmentStops?.find((item: any) => item?.sequenceNo === '1')?.city,
                  state:
                    data?.originState ||
                    data?.loadStop?.find(
                      (item: any) => item?.stopSequence === 1 || item?.sequenceNo === 1,
                    )?.state ||
                    data?.shipmentStops?.find((item: any) => item?.sequenceNo === '1')?.state,
                  radius: data?.originRadius,
                  startDate:
                    data?.pickupStartDate ||
                    commonDateFormat(
                      data?.shipmentStops?.find((item: any) => item?.sequenceNo === '1')
                        ?.actualArrivalDateTime ||
                        data?.shipmentStops?.find((item: any) => item?.sequenceNo === '1')
                          ?.plannedArrivalDateTime,
                      false,
                      false,
                      data?.shipmentStops?.find((item: any) => item?.sequenceNo === '1')?.timeZone
                        ? data?.shipmentStops?.find((item: any) => item?.sequenceNo === '1')
                            ?.timeZone
                        : null,
                    ),
                  endDate:
                    data?.pickupEndDate ||
                    commonDateFormat(
                      data?.shipmentStops?.find((item: any) => item?.sequenceNo === '1')
                        ?.actualDepartureDateTime ||
                        data?.shipmentStops?.find((item: any) => item?.sequenceNo === '1')
                          ?.plannedDepartureDateTime,
                      false,
                      false,
                      data?.shipmentStops?.find((item: any) => item?.sequenceNo === '1')?.timeZone
                        ? data?.shipmentStops?.find((item: any) => item?.sequenceNo === '1')
                            ?.timeZone
                        : null,
                    ),
                  stopInfo:
                    data?.loadStop?.find((item: any) => item?.stopSequence === 1) ||
                    data?.shipmentStops?.find((item: any) => item?.sequenceNo === '1'),
                  showDropDown: showDropDown,
                })}
                {isExpanded &&
                  !isMyLoads &&
                  data?.numberOfStops > 2 &&
                  data?.loadStop?.map((loadItem: any, index: number) => {
                    if (index > 0 && index < data?.numberOfStops - 1) {
                      return (
                        <>
                          <CenterPartSection
                            {...{
                              type: 'stop',
                              imgSrc: '/oo/assets/icon/stop-pointer.svg',
                              city: loadItem?.city,
                              state: loadItem?.state,
                              radius: data?.originRadius,
                              stopInfo: loadItem,
                            }}
                          />
                        </>
                      );
                    }
                  })}
                {isExpanded &&
                  isMyLoads &&
                  data?.numberOfStops > 2 &&
                  data?.shipmentStops?.map((loadItem: any, index: number) => {
                    if (index > 0 && index < data?.numberOfStops - 1) {
                      return (
                        <>
                          <CenterPartSection
                            {...{
                              type: 'stop',
                              imgSrc: '/oo/assets/icon/stop-pointer.svg',
                              city: loadItem?.city,
                              state: loadItem?.state,
                              radius: data?.originRadius,
                              stopInfo: loadItem,
                            }}
                          />
                        </>
                      );
                    }
                  })}
                {CenterPartSection({
                  type: 'destination',
                  imgSrc: '/oo/assets/icon/destination_icon.svg',
                  city:
                    data?.destinationCity ||
                    data?.loadStop?.find((item: any) => item?.stopSequence === data?.numberOfStops)
                      ?.city ||
                    data?.shipmentStops?.find(
                      (item: any) => item?.sequenceNo === data?.numberOfStops,
                    )?.city,
                  state:
                    data?.loadStop?.find((item: any) => item?.stopSequence === data?.numberOfStops)
                      ?.state ||
                    data?.shipmentStops?.find(
                      (item: any) => item?.sequenceNo === data?.numberOfStops,
                    )?.state ||
                    data?.destinationState ||
                    [data?.destinationState1, data?.destinationState2, data?.destinationState3]
                      ?.filter((item: string) => !!item)
                      ?.join(', ') ||
                    data?.shipmentStops?.find(
                      (item: any) => item?.sequenceNo === data?.numberOfStops,
                    )?.state,
                  radius: data?.destinationRadius,
                  startDate:
                    data?.dropStartDate ||
                    commonDateFormat(
                      data?.shipmentStops?.find(
                        (item: any) => item?.sequenceNo === data?.numberOfStops,
                      )?.actualArrivalDateTime ||
                        data?.shipmentStops?.find(
                          (item: any) => item?.sequenceNo === data?.numberOfStops,
                        )?.plannedArrivalDateTime,
                      false,
                      false,
                      data?.shipmentStops?.find(
                        (item: any) => item?.sequenceNo === data?.numberOfStops,
                      )?.timeZone
                        ? data?.shipmentStops?.find(
                            (item: any) => item?.sequenceNo === data?.numberOfStops,
                          )?.timeZone
                        : null,
                    ),
                  endDate:
                    data?.dropEndDate ||
                    commonDateFormat(
                      data?.shipmentStops?.find(
                        (item: any) => item?.sequenceNo === data?.numberOfStops,
                      )?.actualDepartureDateTime ||
                        data?.shipmentStops?.find(
                          (item: any) => item?.sequenceNo === data?.numberOfStops,
                        )?.plannedDepartureDateTime,
                      false,
                      false,
                      data?.shipmentStops?.find(
                        (item: any) => item?.sequenceNo === data?.numberOfStops,
                      )?.timeZone
                        ? data?.shipmentStops?.find(
                            (item: any) => item?.sequenceNo === data?.numberOfStops,
                          )?.timeZone
                        : null,
                    ),
                  stopInfo:
                    data?.loadStop?.find(
                      (item: any) => item?.stopSequence === data?.numberOfStops,
                    ) ||
                    data?.shipmentStops?.find(
                      (item: any) => item?.sequenceNo === data?.numberOfStops,
                    ),
                  showDropDown: showDropDown,
                  showReload: showReload,
                  showRate: showRate,
                })}
              </div>
              {isMobile && showReload && (
                <ReloadButton
                  isExpandedCard={isExpanded}
                  isRecentlyDelivered={isRecentlyDelivered}
                  handleClick={() => {
                    fbLogEvent(EVENT.UE_SEARCH_RELOAD, {
                      screen: SCREEN.SEARCH,
                      tab: logEventLeadFinding(),
                      loadId: data?.loadId,
                      ...(data?.division && { load_divison: data?.division }),
                      ...(data?.rate?.binSources && { load_source_tms: data?.rate?.binSource }),
                      ...(data?.rate?.binRateIdentifier && {
                        load_bin_rate_id: data?.rate?.binRateIdentifier,
                      }),
                      ...(data?.estimatedTotalRpm && {
                        load_estimated_rpm: data?.estimatedTotalRpm,
                      }),
                    });
                    const originStop =
                      data?.loadStop?.find((item: any) => item?.stopSequence === '1') ||
                      data?.shipmentStops?.find((item: any) => item?.sequenceNo === '1');
                    const destinationStop =
                      data?.loadStop?.find(
                        (item: any) => item?.stopSequence === data?.numberOfStops,
                      ) ||
                      data?.shipmentStops?.find(
                        (item: any) => item?.sequenceNo === data?.numberOfStops,
                      );
                    const payload = {
                      originLocation: isRecentlyDelivered
                        ? {
                            City: originStop?.city,
                            State: originStop?.state,
                            Country: originStop?.country,
                            Zip: originStop?.postalCode,
                          }
                        : {
                            City: destinationStop?.city,
                            State: destinationStop?.state,
                            Country: destinationStop?.country,
                            Zip: destinationStop?.postalCode,
                          },
                      destinationLocation: isRecentlyDelivered
                        ? {
                            City: destinationStop?.city,
                            State: destinationStop?.state,
                            Country: destinationStop?.country,
                            Zip: destinationStop?.postalCode,
                          }
                        : {},
                      originRadius: appConfigData?.ORIGIN_RADIUS_INIT?.initialValue,
                      destinationRadius: appConfigData?.DESTINATION_RADIUS_INIT?.initialValue,
                      originFromDate: commonDateFormat(
                        isBeforeCurrentDate(
                          isRecentlyDelivered
                            ? originStop?.appointmentFrom || originStop?.plannedArrivalDateTime
                            : destinationStop?.appointmentFrom ||
                                destinationStop?.plannedArrivalDateTime,
                        )
                          ? dayjs()
                          : isRecentlyDelivered
                            ? originStop?.appointmentFrom || originStop?.plannedArrivalDateTime
                            : destinationStop?.appointmentFrom ||
                              destinationStop?.plannedArrivalDateTime,
                      ) as string,
                      originToDate: commonDateFormat(
                        isBeforeCurrentDate(
                          isRecentlyDelivered
                            ? originStop?.appointmentTo || originStop?.plannedDepartureDateTime
                            : destinationStop?.appointmentTo ||
                                destinationStop?.plannedDepartureDateTime,
                        )
                          ? dayjs().add(pickupDateOffset, 'day')
                          : (isRecentlyDelivered
                              ? originStop?.appointmentTo || originStop?.plannedDepartureDateTime
                              : destinationStop?.appointmentTo ||
                                destinationStop?.plannedDepartureDateTime) &&
                              dayjs(
                                isRecentlyDelivered
                                  ? originStop?.appointmentTo ||
                                      originStop?.plannedDepartureDateTime
                                  : destinationStop?.appointmentTo ||
                                      destinationStop?.plannedDepartureDateTime,
                              ).add(pickupDateOffset, 'day'),
                      ) as string,
                      destinationFromDate: commonDateFormat(
                        isBeforeCurrentDate(
                          destinationStop?.appointmentFrom ||
                            destinationStop?.plannedArrivalDateTime,
                        )
                          ? dayjs()
                          : destinationStop?.appointmentFrom ||
                              destinationStop?.plannedArrivalDateTime,
                      ) as string,
                      destinationToDate: commonDateFormat(
                        isBeforeCurrentDate(
                          destinationStop?.appointmentTo ||
                            destinationStop?.plannedDepartureDateTime,
                        )
                          ? dayjs().add(dropDateOffset, 'day')
                          : (destinationStop?.appointmentTo ||
                              destinationStop?.plannedDepartureDateTime) &&
                              dayjs(
                                destinationStop?.appointmentTo ||
                                  destinationStop?.plannedDepartureDateTime,
                              ).add(dropDateOffset, 'day'),
                      ) as string,
                    };
                    dispatch(updateSearchQuery(payload));
                    dispatch(handleResetSearchQueryError());
                    navigate('/search', { state: { isReload: true } });
                  }}
                />
              )}
            </div>
            {!hideRightPart && (
              <div
                className={`Right_Part${isMyLoads ? ' hidden-border-left' : ''}${data?.extendedNetwork ? ' Extended_Network' : ''}`}
              >
                {!isMyLoads && (
                  <div
                    className={`Right_Part_Top ${hideRightTopPart ? 'hidden' : ''}`}
                    data-testid="Right_Part_Top"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRightTopButtonClick &&
                        handleRightTopButtonClick(data?.id || data?.loadId);
                    }}
                  >
                    {TopRightIcon(
                      data,
                      isMobile,
                      isWatchedLoads,
                      isRecent,
                      isFavorite,
                      isSearchResults,
                      isMyLoads,
                    )}
                  </div>
                )}
                {!isMobile &&
                  (isWatchedLoads || isSearchResults) &&
                  data?.rate?.totalCharge &&
                  !showRateLeft && (
                    <p className="Total_Charges">
                      $
                      {convertCurrencyFormat(
                        Math?.floor(data?.rate?.totalCharge) || data?.rate?.totalCharge,
                      )}
                    </p>
                  )}
                {!isMobile &&
                  (isWatchedLoads || isSearchResults) &&
                  data?.rate?.totalCharge &&
                  !showRateLeft && (
                    <div className="Load_RPM" data-testid="Load_RPM">
                      <span>Loaded RPM: </span>
                      <span data-testid="Load_RPM_Value">
                        {currencyFormatter(data?.costCurrency, data?.ratePerMile)}
                      </span>
                    </div>
                  )}
                {!isMobile &&
                  (isSearchResults || isRecent || isFavorite) &&
                  !(isWatchedLoads || isGlobalSearch || showRateLeft || isMyLoads) &&
                  isValidRPM(data?.estimatedTotalRpm) &&
                  searchQuery?.originLocation?.State?.length > 0 && (
                    <div className="Est_Total_RPM" data-testid="Est_Total_RPM">
                      <span>Est Total RPM: </span>
                      <span data-testid="Est_Total_RPM_Value">
                        {currencyFormatter(data?.costCurrency, data?.estimatedTotalRpm)}
                      </span>
                    </div>
                  )}
                {RightPartBottomButton(
                  isMobile,
                  showBottomButton,
                  isWatchedLoads,
                  isSearchResults,
                  isMyLoads,
                  data?.rate?.totalCharge,
                  isAppointment,
                  handleBottomClick,
                  handleBookNowClick,
                )}
                {size === ELoadCardSize?.large && data?.extendedNetwork && !isMobile && (
                  <div className={`Extended_Network_Indicator${!isMobile ? ' Web' : ''}`}>
                    extended network
                  </div>
                )}
              </div>
            )}
          </div>
          {data?.extendedNetwork && isMobile && (
            <div className={`Extended_Network_Indicator${isMobile ? ' Mobile' : ''}`}>
              extended network
            </div>
          )}
        </div>
        <div
          className={`LoadCard_Expand ${dropDownIndicator === 'down' ? 'Unexpanded' : 'Expanded'}`}
        >
          {!isMobile && dropDownIndicator === 'up' && (
            <ExpandedViewContainer
              data={data}
              isRecent={isRecent}
              isNewSearch={isSearchResults}
              isFavorite={isFavorite}
              isWatchedLoads={isWatchedLoads}
              isMyLoads={isMyLoads}
            />
          )}
          {!isMobile && showDropDown && (
            <div
              className={`Load-Card-Down-Arrow ${dropDownIndicator}`}
              onClick={(e: any) => {
                if (isMyLoads && dropDownIndicator === 'down') {
                  const id = data?.shipmentId;
                  isMyLoads && dispatch(getLoadData({ id }));
                }
                if (dropDownIndicator === 'down') {
                  fbLogEvent(EVENT.SV_VIEW_LOAD_DETAILS, {
                    screen: SCREEN.LOAD_DETAILS,
                    loadId: data?.loadId,
                    lead: logEventLeadFinding(),
                    ...(data?.division && { load_divison: data?.division }),
                    ...(data?.rate?.binSources && { load_source_tms: data?.rate?.binSource }),
                    ...(data?.rate?.binRateIdentifier && {
                      load_bin_rate_id: data?.rate?.binRateIdentifier,
                    }),
                    ...(data?.estimatedTotalRpm && { load_estimated_rpm: data?.estimatedTotalRpm }),
                  });
                }
                setDropDownIndicator(dropDownIndicator === 'down' ? 'up' : 'down');
              }}
            >
              <img src={`/oo/assets/icon/down arrow@2x.png`} alt="Load-Card-Down-Arrow-Icon" />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default LoadCard;
